import { useRef } from 'react';
import { Bold, Italic, Heading2, List, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Markdown } from '@/lib/markdown';
import { cn } from '@/lib/utils';

const QUICK_EMOJI = ['📌', '🚀', '⚠️', '🎉', '✅', '❌', '📅', '🔧', '📢', 'ℹ️'];

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  id?: string;
}

/**
 * Lightweight markdown editor: Edit/Preview tabs, emoji quick-bar, format helpers.
 * No WYSIWYG framework — uses already-installed `react-markdown` for the preview.
 */
export function MarkdownEditor({
  value,
  onChange,
  placeholder = 'Write the announcement in markdown…',
  rows = 12,
  className,
  id,
}: MarkdownEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  function insertAtCaret(insert: string) {
    const ta = textareaRef.current;
    if (!ta) {
      onChange(value + insert);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = value.slice(0, start) + insert + value.slice(end);
    onChange(next);
    // Restore caret after the insert on next tick.
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + insert.length;
      ta.setSelectionRange(pos, pos);
    });
  }

  function wrapSelection(prefix: string, suffix: string = prefix, placeholderInner = 'text') {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.slice(start, end) || placeholderInner;
    const next = value.slice(0, start) + prefix + selected + suffix + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const innerStart = start + prefix.length;
      const innerEnd = innerStart + selected.length;
      ta.setSelectionRange(innerStart, innerEnd);
    });
  }

  function prefixLine(prefix: string) {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    // Find start of current line
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + prefix.length;
      ta.setSelectionRange(pos, pos);
    });
  }

  return (
    <div className={cn('rounded-md border bg-background', className)}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b px-2 py-1.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => wrapSelection('**')}
          title="Bold"
        >
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => wrapSelection('*')}
          title="Italic"
        >
          <Italic className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => prefixLine('## ')}
          title="Heading"
        >
          <Heading2 className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => prefixLine('- ')}
          title="Bulleted list"
        >
          <List className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => wrapSelection('[', '](https://)', 'link text')}
          title="Link"
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </Button>

        <div className="mx-1 h-5 w-px bg-border" />

        {QUICK_EMOJI.map((e) => (
          <button
            key={e}
            type="button"
            className="rounded px-1 py-0.5 text-base hover:bg-muted"
            onClick={() => insertAtCaret(e)}
            aria-label={`Insert ${e}`}
          >
            {e}
          </button>
        ))}
      </div>

      <Tabs defaultValue="edit">
        <TabsList className="m-2">
          <TabsTrigger value="edit">Edit</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="edit" className="p-2 pt-0">
          <Textarea
            ref={textareaRef}
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            className="font-mono text-sm"
          />
          <p className="mt-1 px-1 text-[11px] text-muted-foreground">
            Tip: open your OS emoji picker with Win+. (Windows) or Ctrl+Cmd+Space (Mac).
          </p>
        </TabsContent>

        <TabsContent value="preview" className="p-3 pt-0">
          <div className="min-h-[8rem] rounded-md border bg-muted/30 p-3">
            {value.trim() ? (
              <Markdown>{value}</Markdown>
            ) : (
              <p className="text-sm text-muted-foreground">Nothing to preview yet.</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
